/**
 * Recovery from stale lazy-loaded chunks after a frontend deploy.
 *
 * A deploy replaces every content-hashed chunk. A tab still running the
 * previous entry bundle then asks for a chunk that no longer exists, and the
 * dynamic import rejects. React.lazy caches that rejection, so re-rendering
 * cannot recover - only a full page load (new index.html, new chunk names)
 * does. This module performs that reload once, guarded so a chunk that is
 * genuinely missing on the server cannot loop the tab.
 *
 * @module utils/staleChunkRecovery
 * @see TASK-BUGFIX-STALE-CHUNK-ERROR-PAGE-CHG-001
 */

const RELOAD_STAMP_KEY = "verisa:stale-chunk-reload-at";

/** A second failure within this window after our own reload is not stale - it is broken. */
export const RELOAD_GUARD_MS = 30_000;

// Chromium, Firefox, Safari wordings of a failed dynamic import, plus Vite's CSS preload failure.
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

/** Fallback guard when sessionStorage is unavailable (private mode, blocked storage). */
let reloadedThisPageLoad = false;

/** True when the error is a failed dynamic import of a code chunk. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_PATTERN.test(message);
}

function readStamp(): number | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STAMP_KEY);
    return raw === null ? null : Number(raw);
  } catch {
    return reloadedThisPageLoad ? Date.now() : null;
  }
}

function writeStamp(now: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch (err) {
    console.warn("[staleChunkRecovery] sessionStorage unavailable, guarding in memory:", err);
  }
}

/**
 * Reload the page once to pick up the current deploy.
 *
 * @returns true when a reload was started; false when a reload already happened
 *   within {@link RELOAD_GUARD_MS}, in which case the caller must show the error.
 */
export function reloadForStaleChunk(): boolean {
  const now = Date.now();
  const last = readStamp();
  if (reloadedThisPageLoad || (last !== null && now - last < RELOAD_GUARD_MS)) {
    return false;
  }
  reloadedThisPageLoad = true;
  writeStamp(now);
  window.location.reload();
  return true;
}

/**
 * Listen for Vite's `vite:preloadError`, which the build's preload helper
 * dispatches for every failed lazy import - including ones outside any error
 * boundary. The event is not cancelled: if the reload is refused by the guard,
 * the import still rejects and the nearest error boundary shows the error.
 */
export function installStaleChunkRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    console.warn("[staleChunkRecovery] lazy chunk failed to load:", payload);
    reloadForStaleChunk();
  });
}

/** Test hook: forget the in-memory guard. */
export function __resetStaleChunkRecoveryForTests(): void {
  reloadedThisPageLoad = false;
}
